import React, {
  memo,
  useMemo,
} from 'react';

import {
  Button,
  Tooltip,
  Typography,
} from 'antd';

import { PlusCircleOutlined } from '@ant-design/icons';

import CustomReadMoreContent
  from '../../../components/CustomReadMoreText/CustomReadMoreContent';
import { TQProfileQuestionType } from '../../../types/tqConnectTypes';
import {
  ITQProfileCompletionQueryWithResponse,
} from '../../../types/tqConnectTypes/user';
import TQConnectIcon from '../../TQConnect/components/TQConnectIcon';

interface Props {
  scoreName: string;
  response: ITQProfileCompletionQueryWithResponse;
  hasAnswer: boolean;
  onAddAnswer: () => void;
  canAddAnswer?: boolean;
  isLastUnattempted?: boolean;
}

const ResponseItem: React.FC<Props> = ({
  scoreName,
  response,
  hasAnswer,
  canAddAnswer,
  onAddAnswer,
  isLastUnattempted,
}) => {
  const {
    questionId,
    type,
    question,
    score,
    selectedOptions,
    subjectiveAnswer,
  } = response;

  const renderedAnswer = useMemo(() => {
    if (type === TQProfileQuestionType.TEXT && subjectiveAnswer) {
      return (
        <CustomReadMoreContent lines={5} className="answer__subjective">
          {subjectiveAnswer}
        </CustomReadMoreContent>
      );
    }

    if (selectedOptions?.length) {
      return (
        <div className="answer__options">
          {selectedOptions.map((option) => (
            <Typography.Text
              className="options__item"
              key={`${questionId}-${option}`}>
              {option}
            </Typography.Text>
          ))}
        </div>
      );
    }

    return null;
  }, [type, subjectiveAnswer, selectedOptions, questionId]);

  return (
    <div className="list__item">
      <div className="item__header">
        <Typography.Title level={5} className="header__title">
          {question}{' '}
          {selectedOptions?.length ? `(${selectedOptions.length})` : ''}
        </Typography.Title>
      </div>

      <div className="item__content">
        {hasAnswer ? (
          renderedAnswer
        ) : (
          <Typography.Text type="secondary">
            No response provided.
          </Typography.Text>
        )}
      </div>

      {canAddAnswer ? (
        <div className="item__actions">
          <Tooltip title="Add your response" mouseEnterDelay={0.5}>
            <Typography.Text
              className="actions__text-btn"
              onClick={onAddAnswer}>
              <PlusCircleOutlined />
              <Typography.Text
                type="secondary"
                className="actions__text-btn__text">
                Add your response
              </Typography.Text>
            </Typography.Text>
          </Tooltip>

          <Tooltip
            title={
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {isLastUnattempted ? 'Complete' : 'Submit'} & Earn{' '}
                <TQConnectIcon size={16} /> {score} {scoreName}
              </span>
            }
            mouseEnterDelay={0.5}>
            <Button
              className="siteBtn darkBtn actions__btn"
              onClick={onAddAnswer}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {isLastUnattempted ? 'Complete' : 'Submit'} & Earn{' '}
                <TQConnectIcon size={16} /> {score} {scoreName}
              </span>
            </Button>
          </Tooltip>
        </div>
      ) : null}
    </div>
  );
};

export default memo(ResponseItem);
